import React, { useState } from 'react'
import dayjs from 'dayjs'
import {
  Modal,
  Tag,
  Space,
  Typography,
  Descriptions,
  Badge,
  Button,
  Popconfirm,
  message,
} from 'antd'
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  DeleteOutlined,
  LinkOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import { observer } from 'mobx-react-lite'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ExtendedEvent } from './types'
import { REGULAR_EVENT_TYPES } from './constants'
import { useStore } from '../../stores/root-store-context'
import { toLocalDate } from '../../helpers/dateConverters'
import { Roles } from '../../enums/Roles'

const { Text } = Typography
const WEEKDAY = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const unitByPattern = {
  none: 'event',
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
  yearly: 'year'
}

const fmtRec = (r?: ExtendedEvent['recurrence'], t?: any): string | null => {
  if (!r) return null
  const p = r.pattern as unknown
  if (p === 'none' || (typeof p === 'number' && p === 0)) return null
  const unit =
    typeof p === 'string'
      ? unitByPattern[p as keyof typeof unitByPattern]
      : ['event', 'day', 'week', 'month', 'year'][p as number]
  if (!unit) return null
  const head =
    r.interval && r.interval > 1
      ? t('EveryN', { interval: r.interval, unit })
      : `${t('Each')} ${unit}`
  const isWeekly = p === 'weekly' || (typeof p === 'number' && p === 2)
  return isWeekly && r.byWeekday?.length
    ? `${head} ${r.byWeekday.map(d => t(WEEKDAY[d])).join(', ')}`
    : head
}

const StatusTag: React.FC<{ status: 'pending' | 'approved' | 'rejected' }> = ({ status }) => {
  const { t } = useTranslation()
  return (
    <Tag
      color={status === 'pending' ? 'orange' : status === 'rejected' ? 'red' : 'green'}
      icon={status === 'approved' ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
    >
      {t(status.charAt(0).toUpperCase() + status.slice(1))}
    </Tag>
  )
}

function stripHtml(html?: string): string {
  if (!html) return ''
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

interface Props {
  event: ExtendedEvent | null
  onClose: () => void
  onEditOne: (ev: ExtendedEvent) => void
  onEditSeries: (ev: ExtendedEvent) => void
}

const EventDetailsModal: React.FC<Props> = observer(({ event, onClose, onEditOne, onEditSeries }) => {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const { employeesStore, projectEventStore, applicationStore } = useStore()
  if (!event) return null

  const start = event.type === 'project' ? dayjs(toLocalDate(event.start)) : dayjs(event.start)
  const end = event.type === 'project' ? dayjs(toLocalDate(event.end)) : dayjs(event.end)
  const sameDay = start.isSame(end, 'day')
  const durationH = Math.round(end.diff(start, 'minute') / 6) / 10
  const author =
    event.userName ??
    (() => {
      const u = employeesStore.users.find(x => String(x.id) === String(event.userId))
      return u ? `${u.firstName} ${u.lastName}` : '—'
    })()

  const pts = event.participants?.length
    ? event.participants
      .map(pid => {
        const pidStr = String(pid)
        const u = employeesStore.users.find(x => String(x.id) === pidStr)
        return u ? `${u.firstName} ${u.lastName}` : `#${pidStr}`
      })
      .join(', ')
    : '—'

  const recText = fmtRec(event.recurrence, t)
  const rawStatus = event.status ?? 'approved'
  const status: 'pending' | 'approved' | 'rejected' =
    typeof rawStatus === 'number' ? (['pending', 'approved', 'rejected'][rawStatus] as any) : rawStatus

  const canApprove =
    event.type === 'project' &&
    event.needsApproval &&
    status === 'pending' &&
    end.isBefore(dayjs()) &&
    Number(applicationStore.user.id) === Number(event.userId)

  const canDelete =
    applicationStore.user?.roles?.some(r => r.id === Roles.Admin) ||
    Number(event.userId) === Number(applicationStore.user.id)

  const sanitizedDescription = stripHtml(event.description)
  const onlineMeetingUrl = event.onlineMeetingUrl

  const approve = async () => {
    try {
      setSaving(true)
      await projectEventStore.approveAttendance(event.id)
      message.success(t('AttendanceApproved'))
      onClose()
    } catch {
      message.error(t('AttendanceApproveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const deleteCurrent = async () => {
    const eventIdStr = String(event.id)
    const isVirtual = eventIdStr.includes('_')

    if (isVirtual) {
      await projectEventStore.deleteGeneratedOccurrence(event)
    } else {
      const isProjectEvent = eventIdStr.startsWith('project_')

      if (isProjectEvent || event.type === 'project') {
        const numericId = eventIdStr.replace(/^project_/, '')
        await projectEventStore.deleteById(numericId, !!event.parentEventId, event.parentEventId)
      } else {
        await projectEventStore.deleteById(eventIdStr, !!event.parentEventId, event.parentEventId)
      }
    }
    message.success(t('Deleted'))
    onClose()
  }

  const deleteSeries = async () => {
    const masterId = event.parentEventId ?? event.id
    const masterIdStr = String(masterId).replace(/^project_/, '')
    await projectEventStore.deleteById(masterIdStr, false)
    message.success(t('Deleted'))
    onClose()
  }

  const regCfg = REGULAR_EVENT_TYPES.find(c => c.type === event.eventType)
  const headerIcon = event.type === 'project' ? '📋' : regCfg?.icon ?? '📅'
  const isPast = end.isBefore(dayjs().startOf('day'))
  const crmId = event.type === 'crm' ? String(event.id).replace(/^crm_/, '') : ''
  const buttonsStyles = { margin: 5 }

  return (
    <Modal
      open
      onCancel={onClose}
      width={560}
      footer={
        <>
          {event.type === 'crm' && (
            <Link to={`/${applicationStore.company?.Tenant}/crm/event/${crmId}`}>
              <Button type="primary">{t('GoToEvent')}</Button>
            </Link>
          )}

          {!isPast && event.type === 'project' && (
            <>
              <Button onClick={() => onEditOne(event)} style={buttonsStyles}>
                {t('EditCurrent')}
              </Button>
              <Button onClick={() => onEditSeries(event)} type="dashed" style={buttonsStyles}>
                {t('EditSeries')}
              </Button>
            </>
          )}

          {canDelete && event.type === 'project' && (
            <>
              <Popconfirm title={t('DeleteOccurrenceConfirm')} onConfirm={deleteCurrent}>
                <Button danger icon={<DeleteOutlined />} style={buttonsStyles}>
                  {t('DeleteCurrent')}
                </Button>
              </Popconfirm>

              {event.recurrence && event.recurrence.pattern !== 'none' && (
                <Popconfirm title={t('DeleteSeriesConfirm')} onConfirm={deleteSeries}>
                  <Button danger type="dashed" icon={<DeleteOutlined />}>
                    {t('DeleteSeries')}
                  </Button>
                </Popconfirm>
              )}
            </>
          )}

          {canApprove && (
            <Popconfirm title={t('ApproveAttendanceQuestion')} onConfirm={approve}>
              <Button type="primary" loading={saving} style={buttonsStyles}>
                {t('ApproveAttendance')}
              </Button>
            </Popconfirm>
          )}
        </>
      }
      title={
        <Space>
          {headerIcon} {event.title}
        </Space>
      }
    >
      <Descriptions column={1} size="small" bordered labelStyle={{ width: 150, fontWeight: 600 }}>
        <Descriptions.Item label={t('Author')}>{author}</Descriptions.Item>

        {event.type === 'project' && (
          <Descriptions.Item label={t('Project')}>{event.projectName ?? `#${event.projectId}`}</Descriptions.Item>
        )}

        <Descriptions.Item label={t('Start')}>
          <Space>
            <CalendarOutlined />
            {start.format('YYYY-MM-DD')}
            {!sameDay && ` / ${end.format('YYYY-MM-DD')}`}
          </Space>
          <br />
          <Space style={{ marginLeft: 16 }}>
            <ClockCircleOutlined />
            {start.format('HH:mm')} – {end.format('HH:mm')}
            {!Number.isNaN(durationH) && ` (${durationH}${t('HoursAbbr')})`}
          </Space>
        </Descriptions.Item>

        {event.type === 'regular' && (
          <Descriptions.Item label={t('Type')}>
            <Tag color="blue">
              {regCfg?.icon} {t(event.eventType ?? '')}
            </Tag>
          </Descriptions.Item>
        )}

        {event.type === 'project' && (
          <>
            <Descriptions.Item label={t('Status')}>
              {event.needsApproval ? <StatusTag status={status} /> : <StatusTag status="approved" />}
            </Descriptions.Item>
            <Descriptions.Item label={t('Participants')}>{pts}</Descriptions.Item>
          </>
        )}

        {recText && (
          <Descriptions.Item label={t('Recurrence')}>
            <Badge color="processing" text={recText} />
          </Descriptions.Item>
        )}

        {sanitizedDescription && (
          <Descriptions.Item label={t('Description')}>
            <Text>{sanitizedDescription}</Text>
          </Descriptions.Item>
        )}

        {(event.isOnlineMeeting || onlineMeetingUrl) && (
          <Descriptions.Item label={t('OnlineMeeting')}>
            <Space>
              <VideoCameraOutlined />
              <Text>{t('OnlineMeeting')}</Text>
            </Space>
            {(onlineMeetingUrl ?? event.onlineMeetingUrl) && (
              <>
                <br />
                <a href={onlineMeetingUrl ?? event.onlineMeetingUrl} target="_blank" rel="noopener noreferrer">
                  <Space>
                    <LinkOutlined />
                    {t('JoinMeeting')}
                  </Space>
                </a>
              </>
            )}
          </Descriptions.Item>
        )}
      </Descriptions>
    </Modal>
  )
})

export default EventDetailsModal